import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { setAuthToken, authService, impersonationService } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { UserProfile } from '../types/user';
import {
  clearImpersonationSession,
  getImpersonationToken,
  getImpersonationUser,
  setImpersonationSession,
} from '../utils/impersonation';

interface AuthContextType {
  user: UserProfile | null;
  login: (username: string, password: string) => Promise<string>;
  logout: () => void;
  /** True while viewing the app as another user (read-only session). */
  impersonating: boolean;
  /** The real (admin) user behind an impersonation session, else null. */
  impersonator: UserProfile | null;
  startImpersonation: (userId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [impersonator, setImpersonator] = useState<UserProfile | null>(null);
  // Derived from storage, not from `impersonator`, which can be null if the
  // stored admin profile is missing while an impersonation token is active.
  const [impersonating, setImpersonating] = useState<boolean>(
    () => getImpersonationToken() !== null,
  );

  useEffect(() => {
    // An impersonation session (per-tab) takes precedence: `user` becomes the
    // impersonated target so all role-gated UI renders as they would see it,
    // while the admin's own profile is kept aside as `impersonator`.
    const impersonatedUser = getImpersonationUser();
    if (impersonatedUser && getImpersonationToken()) {
      setUser(impersonatedUser);
      const storedAdmin = localStorage.getItem('user');
      if (storedAdmin) {
        setImpersonator(JSON.parse(storedAdmin));
      }
      return;
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<string> => {
    try {
      const response = await authService.login(username, password);
      const { token } = response.data;

      const decodedToken: UserProfile = jwtDecode(token);
      setUser(decodedToken);
      localStorage.setItem('user', JSON.stringify(decodedToken));
      setAuthToken(token);

      // Store token based on rememberMe preference
      localStorage.setItem('token', token);
      sessionStorage.setItem('token', token);

      return token;

      // Token is stored based on rememberMe preference
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          throw new Error('รหัสผู้ใช้หรือรหัสผ่านไม่ถูกต้อง');
        } else {
          throw new Error('เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง');
        }
      } else {
        throw new Error('เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง');
      }
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setImpersonator(null);
    setImpersonating(false);
    clearImpersonationSession();
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/login';
  }, []);

  const startImpersonation = useCallback(async (userId: number): Promise<void> => {
    const response = await impersonationService.start(userId);
    const { token } = response.data;
    const decoded: UserProfile = jwtDecode(token);

    setImpersonationSession(token, decoded);
    setAuthToken(token);
    setImpersonating(true);

    const storedAdmin = localStorage.getItem('user');
    if (storedAdmin) {
      setImpersonator(JSON.parse(storedAdmin));
    }
    setUser(decoded);
    // Hard navigation: remount the whole app so every view refetches with the
    // impersonated identity (no stale admin-scoped data or caches).
    window.location.href = '/';
  }, []);

  const stopImpersonation = useCallback(async (): Promise<void> => {
    // Best-effort audit of the stop — exiting must work even if it fails.
    try {
      await impersonationService.stop();
    } catch {
      // ignored: the token expires on its own; only the stop audit row is lost
    }
    clearImpersonationSession();
    setAuthToken(localStorage.getItem('token'));
    setImpersonating(false);
    setImpersonator(null);
    const storedAdmin = localStorage.getItem('user');
    setUser(storedAdmin ? JSON.parse(storedAdmin) : null);
    window.location.href = '/';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        impersonating,
        impersonator,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
